import { useEffect, useRef, useState } from "react";
import {
    Box,
    ButtonGroup,
    Divider,
    IconButton,
    Paper,
    TextField,
    Tooltip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import CodeIcon from "@mui/icons-material/Code";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SharedFieldInputProps } from "./types";

type RichHtmlCommand = "bold" | "italic" | "underline" | "insertUnorderedList" | "insertOrderedList";

interface RichHtmlFieldInputProps extends SharedFieldInputProps<string | null> {
    minHeight?: number;
    ariaLabelledBy?: string;
}

const EMPTY_HTML = "";

export function RichHtmlFieldInput({
    value,
    onChange,
    error = false,
    helperText,
    autoFocus = false,
    fullWidth = true,
    disabled = false,
    minHeight = 220,
    ariaLabelledBy,
}: RichHtmlFieldInputProps) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const [sourceMode, setSourceMode] = useState(false);
    const htmlValue = value ?? EMPTY_HTML;

    useEffect(() => {
        if (!editorRef.current || sourceMode) {
            return;
        }

        if (editorRef.current.innerHTML !== htmlValue) {
            editorRef.current.innerHTML = htmlValue;
        }
    }, [htmlValue, sourceMode]);

    useEffect(() => {
        if (autoFocus && !sourceMode) {
            editorRef.current?.focus();
        }
    }, [autoFocus, sourceMode]);

    const runCommand = (command: RichHtmlCommand, commandValue?: string) => {
        if (disabled) {
            return;
        }

        editorRef.current?.focus();
        document.execCommand(command, false, commandValue);
        onChange(editorRef.current?.innerHTML ?? EMPTY_HTML);
    };

    const insertLink = () => {
        if (disabled) {
            return;
        }

        const url = window.prompt("URL");
        if (!url) {
            return;
        }

        editorRef.current?.focus();
        document.execCommand("createLink", false, url);
        onChange(editorRef.current?.innerHTML ?? EMPTY_HTML);
    };

    const toggleSourceMode = () => {
        setSourceMode((current) => !current);
    };

    const toolbarButtonSx = {
        width: 34,
        height: 34,
        borderRadius: 0,
    };

    return (
        <Box sx={{ width: fullWidth ? "100%" : undefined }}>
            <Paper
                variant="outlined"
                sx={{
                    borderColor: error ? "error.main" : "divider",
                    overflow: "hidden",
                    bgcolor: disabled ? "action.disabledBackground" : "background.paper",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 0.75,
                        py: 0.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.default",
                    }}
                >
                    <ButtonGroup variant="text" size="small" disabled={disabled || sourceMode}>
                        <Tooltip title="Bold">
                            <IconButton sx={toolbarButtonSx} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("bold")}>
                                <FormatBoldIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Italic">
                            <IconButton sx={toolbarButtonSx} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("italic")}>
                                <FormatItalicIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Underline">
                            <IconButton sx={toolbarButtonSx} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("underline")}>
                                <FormatUnderlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ButtonGroup>
                    <Divider orientation="vertical" flexItem />
                    <ButtonGroup variant="text" size="small" disabled={disabled || sourceMode}>
                        <Tooltip title="Bullet list">
                            <IconButton sx={toolbarButtonSx} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertUnorderedList")}>
                                <FormatListBulletedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Numbered list">
                            <IconButton sx={toolbarButtonSx} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertOrderedList")}>
                                <FormatListNumberedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Link">
                            <IconButton sx={toolbarButtonSx} onMouseDown={(event) => event.preventDefault()} onClick={insertLink}>
                                <InsertLinkIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ButtonGroup>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={sourceMode ? "Visual editor" : "HTML source"}>
                        <IconButton sx={toolbarButtonSx} onClick={toggleSourceMode} disabled={disabled}>
                            {sourceMode ? <VisibilityIcon fontSize="small" /> : <CodeIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {sourceMode ? (
                    <TextField
                        inputProps={{ "aria-labelledby": ariaLabelledBy }}
                        value={htmlValue}
                        onChange={(event) => onChange(event.target.value)}
                        disabled={disabled}
                        multiline
                        minRows={8}
                        fullWidth
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                fontFamily: "monospace",
                                fontSize: 13,
                                p: 1.25,
                                minHeight,
                                alignItems: "flex-start",
                            },
                        }}
                    />
                ) : (
                    <Box
                        ref={editorRef}
                        role="textbox"
                        aria-multiline="true"
                        aria-labelledby={ariaLabelledBy}
                        contentEditable={!disabled}
                        suppressContentEditableWarning
                        onInput={(event) => onChange((event.currentTarget as HTMLDivElement).innerHTML)}
                        tabIndex={disabled ? -1 : 0}
                        data-autofocus={autoFocus ? "true" : undefined}
                        sx={{
                            minHeight,
                            maxHeight: 520,
                            overflow: "auto",
                            px: 1.25,
                            py: 1,
                            outline: "none",
                            cursor: disabled ? "not-allowed" : "text",
                            "&:focus": {
                                boxShadow: "inset 0 0 0 1px",
                                boxShadowColor: error ? "error.main" : "primary.main",
                            },
                            "& p": {
                                my: 0.75,
                            },
                        }}
                    />
                )}
            </Paper>
            {(helperText || error) && (
                <Box
                    sx={{
                        mt: 0.5,
                        mx: 1.75,
                        color: error ? "error.main" : "text.secondary",
                        fontSize: 12,
                    }}
                >
                    {helperText}
                </Box>
            )}
        </Box>
    );
}
